<template>
  <hr-list-select
    v-bind="$attrs"
    ref="input"
    :disable="disable"
    :options="countryList"
    :readonly="readonly"
    :model-value="modelValue"
    :style="widthLimit !== 'auto' ? { width: widthLimit } : undefined"
    @update:model-value="handleChange"
  >
    <template
      v-if="showFlag"
      #option-leading="{ opt }"
    >
      <hr-country-flag
        v-if="opt"
        :country="opt.value"
      />
    </template>
    <template
      v-if="showFlag && modelValue"
      #prepend
    >
      <!--
        Direct child of the slot, which the field lays out as a centred flex row. Wrapping it makes
        the wrapper the item and leaves the flag sitting on a line box inside it, high against a
        field two lines tall.
      -->
      <hr-country-flag
        class="q-px-xs"
        :country="modelValue"
      />
    </template>
    <template #selected-item>
      {{ getCountryLabel(modelValue) }}
    </template>
    <template
      v-if="appendImage"
      #append
    >
      <hr-avatar
        size="xs"
        :src="appendImage"
      />
    </template>
  </hr-list-select>
</template>
<script>
import { countries } from 'boot/countries';
import { getCountryCallingCode } from 'libphonenumber-js';
import { HrAvatar, HrCountryFlag, HrListSelect } from 'src/design-system';

export default {
  name: 'CountrySelect',

  components: { HrAvatar, HrCountryFlag, HrListSelect },

  inheritAttrs: false,

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    showFlag: {
      type: Boolean,
      default: true,
    },
    showName: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'country',
    },
    widthLimit: {
      type: String,
      default: 'auto',
    },
    disable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    appendImage: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      topCountries: ['USA', 'CAN', 'GBR', 'DEU', 'AUS'],
    };
  },

  computed: {
    countryList() {
      // getAlpha3Codes() maps alpha-3 keys to alpha-2 values; options use the alpha-2 value.
      const topSet = new Set(this.topCountries);
      const entries = Object.entries(countries.getAlpha3Codes());
      const toOption = ([, alpha2Code]) => ({
        label: this.showName ? this.normalizeCountryLabel(this.$t(alpha2Code)) : '',
        value: alpha2Code,
      });

      const topCountriesList = entries
        .filter(([alpha3Code]) => topSet.has(alpha3Code))
        .sort(([a], [b]) => this.topCountries.indexOf(a) - this.topCountries.indexOf(b))
        .map(toOption);

      const otherCountriesList = entries
        .filter(([alpha3Code]) => !topSet.has(alpha3Code))
        .map(toOption)
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

      // Label-less groups render top countries first, then a divider, then the rest.
      return [
        { options: topCountriesList },
        { options: otherCountriesList },
      ];
    },

    flatCountryList() {
      return this.countryList.flatMap((group) => group.options);
    },
  },

  methods: {
    getCallingCode(value = null) {
      const countryCode = value || this.modelValue;
      try {
        return `+${getCountryCallingCode(countryCode)}`;
      } catch (err) {
        return '';
      }
    }, // getCallingCode()

    handleChange(newVal) {
      this.$emit('update:modelValue', newVal);
    }, // handleChange()

    getCountryLabel(value) {
      if (this.type === 'callingCode') {
        try {
          return `+${getCountryCallingCode(value)}`;
        } catch (err) {
          return '';
        }
      }

      return this.flatCountryList.find((option) => option.value === value)?.label ?? '';
    }, // getCountryLabel()

    normalizeCountryLabel(label) {
      return Array.isArray(label) ? label.join(', ') : label;
    },

    validate(...args) {
      return this.$refs.input.validate(...args);
    },
  },

};
</script>
